import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useGetDeviceListingQuery } from "../../../api/network/camerasAdminVMApiService";
import { setMapStateData } from "../../../api/store/commonSlice";
import { CameraListingPayload } from "../../../api/types/Camera";
import { APP_CONFIG } from "../../../constants/constants";
import { TMapState, TMapType } from "../../../types/map";
import { TMapRef } from "../../map-fleet/type";
import ScreenCameraManagement from "../management/camera-management";
import ScreenCameraVideoHistoryEvents from "../video-history/video-history-events";
import OnlineCamerasOverview from "./onlineCamerasOverview";
import TripSummary from "./tripSummary";

const ScreenCameraDashboard: React.FC = () => {
  const { t: tMain } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.maps",
  });
  const dispatch = useDispatch();
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );

  // common map ref to be used for various map operations
  const mapRef = React.useRef<TMapRef>({
    map: null,
    objects: {
      mPushpins: [],
      mClusterLayer: null,
    },
  });

  // used for any map state transition to refresh map entities
  const [mapStateTransitionInProgress, setMapStateTransitionInProgress] =
    useState(false);

  const loadResetMapDataWithInitialValues = () => {
    if (APP_CONFIG.DEBUG.MAPS)
      console.log("[loadResetMapDataWithInitialValues]");
    setMapStateTransitionInProgress(true);
    const newMapState: Partial<TMapState> = {
      // ...mapState,
      // mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
      //   .type as TMapType,
      mapCenter: APP_CONFIG.MAPS.DEFAULTS.CENTER,
      mapData: {
        centerPosition: APP_CONFIG.MAPS.DEFAULTS.CENTER,
        ready: true,
      },
    };
    if (APP_CONFIG.DEBUG.MAPS) console.log("newMapState", newMapState);
    setTimeout(() => {
      dispatch(setMapStateData(newMapState));
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  /**
   * reset map type on mount
   * -----------------------
   * Before Sep 23 2024, we were trying to set map type to road on unmount,
   * but it was causing issues with mapdata as rest of the map data
   * including mapScriptLoaded was getting erased.
   * This was happening because `mapState` (store data) was unavailable on unmount.
   */
  useEffect(() => {
    dispatch(
      setMapStateData({
        // ...mapState,
        mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
          .type as TMapType,
      })
    );
    loadResetMapDataWithInitialValues();
  }, []);

  const [cameraManagementQueryParams, setCameraManagementQueryParams] =
    useState<CameraListingPayload>({
      keyword: "",
      model: "",
      status: "",
      offset: 0,
      limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      // group_ids: selectedGroups?.length ? selectedGroups.join(",") : "",
      // camera_ids: selectedCameras?.length ? selectedCameras.join(",") : "",
    });

  // Fetch camera listing data
  const {
    data: dataDeviceListing,
    isFetching: isFetchingDeviceListing,
    error,
    refetch,
  } = useGetDeviceListingQuery(
    {
      ...cameraManagementQueryParams,
      offset: cameraManagementQueryParams.offset,
      // (cameraManagementQueryParams.page - 1) *
      // cameraManagementQueryParams.limit,
    },
    {
      refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
    }
  );

  const { count, data: results } = dataDeviceListing || {};

  const onlineCameras =
    results?.filter((item) => item.is_online === "online")?.length || 0;
  const totalCameras =
    results?.filter((item) => item.cid !== null)?.length || 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Map showing last reported event for every camera id */}
      <ScreenCameraManagement displayAsDashWidget={true} />

      {/* Widgets in 2 columns */}
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-6">
          <OnlineCamerasOverview />
        </div>
        <div className="col-span-6">
          <TripSummary />
        </div>
      </div>

      {/* Fleet Highlights */}
      <ScreenCameraVideoHistoryEvents displayAsDashWidget={true} />
    </div>
  );
};

export default ScreenCameraDashboard;
